<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import LogoIcon from './LogoIcon.vue'

const { t, locale } = useI18n()
const isScrolled = ref(false)
const isMobileMenuOpen = ref(false)

const toggleLang = () => {
  locale.value = locale.value === 'zh-CN' ? 'en-US' : 'zh-CN'
}

const currentLangLabel = computed(() => {
  return locale.value === 'zh-CN' ? t('language.en') : t('language.zh')
})

const navLinks = [
  { key: 'product', href: '#product' },
  { key: 'solutions', href: '#solutions' },
  { key: 'terminals', href: '#terminals' }
]

const portalLinks = [
  { key: 'admin', href: '/admin' },
  { key: 'client', href: '/customer' }
]

const handleScroll = () => {
  isScrolled.value = window.scrollY > 40
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <header
    :class="['navbar', { 'navbar--scrolled': isScrolled }]"
    role="banner"
  >
    <div class="navbar__inner container">
      <a href="#" class="navbar__brand" aria-label="JMTMS">
        <LogoIcon class="navbar__logo" />
        <span class="navbar__name">积米 JMTMS</span>
      </a>

      <nav class="navbar__nav" role="navigation" aria-label="Main">
        <a
          v-for="link in navLinks"
          :key="link.key"
          :href="link.href"
          class="navbar__link"
        >
          {{ t(`nav.${link.key}`) }}
        </a>
      </nav>

      <div class="navbar__actions">
        <div class="navbar__portal-wrap">
          <button
            class="navbar__portal-btn"
            type="button"
            aria-haspopup="true"
            aria-label="管理系统"
          >
            <svg class="navbar__portal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" />
            </svg>
            <span>{{ t('nav.portal') }}</span>
            <svg class="navbar__portal-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div class="navbar__dropdown" role="menu">
            <a
              v-for="portal in portalLinks"
              :key="portal.key"
              :href="portal.href"
              class="navbar__dropdown-item"
              role="menuitem"
            >
              <span class="navbar__dropdown-icon" aria-hidden="true">
                <svg v-if="portal.key === 'admin'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                </svg>
              </span>
              <span class="navbar__dropdown-text">
                <strong>{{ t(`nav.portal${portal.key.charAt(0).toUpperCase()}${portal.key.slice(1)}`) }}</strong>
                <em>{{ t(`nav.portal${portal.key.charAt(0).toUpperCase()}${portal.key.slice(1)}Desc`) }}</em>
              </span>
              <svg class="navbar__dropdown-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="7" y1="17" x2="17" y2="7" />
                <polyline points="7 7 17 7 17 17" />
              </svg>
            </a>
          </div>
        </div>

        <button
          class="navbar__lang"
          @click="toggleLang"
          :aria-label="`Switch to ${currentLangLabel}`"
        >
          {{ currentLangLabel }}
        </button>
        <a href="#contact" class="btn btn-primary navbar__cta">
          {{ t('nav.contact') }}
        </a>

        <button
          class="navbar__mobile-toggle"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
          :aria-expanded="isMobileMenuOpen"
          aria-label="Toggle menu"
        >
          <span />
          <span />
        </button>
      </div>
    </div>

    <div
      :class="['navbar__mobile', { 'navbar__mobile--open': isMobileMenuOpen }]"
      role="dialog"
      aria-modal="true"
    >
      <a
        v-for="link in navLinks"
        :key="link.key"
        :href="link.href"
        class="navbar__mobile-link"
        @click="isMobileMenuOpen = false"
      >
        {{ t(`nav.${link.key}`) }}
      </a>
      <hr />
      <a
        v-for="portal in portalLinks"
        :key="portal.key"
        :href="portal.href"
        class="navbar__mobile-link"
        target="_self"
      >
        {{ t(`nav.${portal.key}`) }}
      </a>
      <hr />
      <a href="#contact" class="btn btn-primary" @click="isMobileMenuOpen = false">
        {{ t('nav.contact') }}
      </a>
    </div>
  </header>
</template>

<style scoped>
.navbar {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 1000;
  padding-block: 1rem;
  transition: background var(--duration-fast) var(--ease-out-expo),
    box-shadow var(--duration-fast) var(--ease-out-expo),
    padding var(--duration-fast) var(--ease-out-expo);
}

.navbar--scrolled {
  background: rgba(250, 249, 247, 0.82);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
  padding-block: 0.75rem;
}

.navbar__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.navbar__brand {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  color: var(--color-dark);
  flex-shrink: 0;
}

.navbar__logo {
  width: 32px;
  height: 32px;
  color: var(--color-dark);
}

.navbar__name {
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.navbar__nav {
  display: flex;
  align-items: center;
  gap: 2.5rem;
}

.navbar__link {
  position: relative;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  transition: color var(--duration-fast) var(--ease-out-expo);
}

.navbar__link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -4px;
  width: 0;
  height: 1.5px;
  background: var(--color-accent);
  transition: width var(--duration-fast) var(--ease-out-expo);
}

.navbar__link:hover {
  color: var(--color-text);
}

.navbar__link:hover::after {
  width: 100%;
}

.navbar__actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.navbar__portal-wrap {
  position: relative;
}

.navbar__portal-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 40px;
  padding: 0 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out-expo),
    border-color var(--duration-fast) var(--ease-out-expo),
    color var(--duration-fast) var(--ease-out-expo);
}

.navbar__portal-btn:hover {
  background: #fff;
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.navbar__portal-icon {
  width: 16px;
  height: 16px;
  color: var(--color-accent);
}

.navbar__portal-chevron {
  width: 12px;
  height: 12px;
  transition: transform var(--duration-fast) var(--ease-out-expo);
}

.navbar__portal-wrap:hover .navbar__portal-chevron {
  transform: rotate(180deg);
}

.navbar__dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 280px;
  background: #ffffff;
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 16px 48px rgba(13, 18, 24, 0.12),
    0 2px 8px rgba(13, 18, 24, 0.06);
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-6px);
  transition: opacity 180ms ease, transform 180ms ease, visibility 0s linear 180ms;
  pointer-events: none;
}

.navbar__portal-wrap:hover .navbar__dropdown,
.navbar__portal-wrap:focus-within .navbar__dropdown {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  pointer-events: auto;
  transition: opacity 180ms ease, transform 180ms ease, visibility 0s;
}

.navbar__dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 12px 14px;
  border-radius: 10px;
  color: var(--color-dark);
  text-decoration: none;
  transition: background var(--duration-fast) var(--ease-out-expo);
}

.navbar__dropdown-item:hover {
  background: var(--color-surface);
}

.navbar__dropdown-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: var(--color-surface);
  display: grid;
  place-items: center;
  color: var(--color-accent);
}

.navbar__dropdown-icon svg {
  width: 18px;
  height: 18px;
}

.navbar__dropdown-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.navbar__dropdown-text strong {
  font-weight: 600;
  font-size: 0.9375rem;
  line-height: 1.3;
  color: var(--color-text);
}

.navbar__dropdown-text em {
  font-style: normal;
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  line-height: 1.3;
}

.navbar__dropdown-arrow {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: var(--color-text-tertiary);
  transition: color var(--duration-fast) var(--ease-out-expo),
    transform var(--duration-fast) var(--ease-out-expo);
}

.navbar__dropdown-item:hover .navbar__dropdown-arrow {
  color: var(--color-accent);
  transform: translate(2px, -2px);
}

.navbar__lang {
  padding: 0.5rem 0.875rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  transition: background var(--duration-fast) var(--ease-out-expo),
    color var(--duration-fast) var(--ease-out-expo);
}

.navbar__lang:hover {
  background: var(--color-surface);
  color: var(--color-text);
}

.navbar__cta {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
}

.navbar__mobile-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  padding: 0.5rem;
}

.navbar__mobile-toggle span {
  display: block;
  width: 100%;
  height: 2px;
  background: var(--color-dark);
  border-radius: 2px;
  transition: transform var(--duration-fast) var(--ease-out-expo);
}

.navbar__mobile {
  position: fixed;
  inset: 64px 0 auto 0;
  background: rgba(250, 249, 247, 0.96);
  backdrop-filter: blur(20px);
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transform: translateY(-120%);
  opacity: 0;
  transition: transform var(--duration-fast) var(--ease-out-expo),
    opacity var(--duration-fast) var(--ease-out-expo);
  pointer-events: none;
}

.navbar__mobile--open {
  transform: translateY(0);
  opacity: 1;
  pointer-events: auto;
}

.navbar__mobile-link {
  font-size: 1.25rem;
  font-weight: 600;
  padding-block: 0.5rem;
}

.navbar__mobile hr {
  border: none;
  border-top: 1px solid var(--color-border);
  margin-block: 0.5rem;
}

@media (max-width: 900px) {
  .navbar__nav,
  .navbar__cta,
  .navbar__portal-wrap {
    display: none;
  }

  .navbar__mobile-toggle {
    display: flex;
  }
}
</style>
